// Reception dashboard — shared vocabulary (RON-5469).
//
// Backend contract (READ-ONLY, the Laravel team's folder — never write to it):
//   D:\laragon\www\laravel-migration\docs\RON-5460\RON-5460_frontend-reception.md
// Section references (§3, §4 …) below point at that document.
//
// The prototype this mirrors:
//   D:\laragon\www\visitor-management-prototype\prototypes\reception-dashboard.html

// ── The seven filters (§3) ───────────────────────────────────────────────────────────────────
//
// "Anything else is a 422 listing all seven, not an empty list — '0 expected' would read to a
// receptionist as a fact about their day."
//
// `all` is a REAL value, not the absence of one, so it is never special-cased away before the
// request is built.
export const RECEPTION_FILTER = {
  ALL: "all",
  EXPECTED: "expected",
  ON_SITE: "on_site",
  WALK_INS: "walk_ins",
  VIP: "vip",
  CONTRACTORS: "contractors",
  OVERSTAY: "overstay",
};

export const RECEPTION_FILTERS = Object.values(RECEPTION_FILTER);

// Tab order is the prototype's, left to right.
export const FILTER_TAB_ORDER = [
  RECEPTION_FILTER.ALL,
  RECEPTION_FILTER.EXPECTED,
  RECEPTION_FILTER.ON_SITE,
  RECEPTION_FILTER.WALK_INS,
  RECEPTION_FILTER.VIP,
  RECEPTION_FILTER.CONTRACTORS,
  RECEPTION_FILTER.OVERSTAY,
];

const FILTER_LABEL_KEYS = {
  [RECEPTION_FILTER.ALL]: "react_reception_filter_all",
  [RECEPTION_FILTER.EXPECTED]: "react_reception_filter_expected",
  [RECEPTION_FILTER.ON_SITE]: "react_reception_filter_on_site",
  [RECEPTION_FILTER.WALK_INS]: "react_reception_filter_walk_ins",
  [RECEPTION_FILTER.VIP]: "react_reception_filter_vip",
  [RECEPTION_FILTER.CONTRACTORS]: "react_reception_filter_contractors",
  [RECEPTION_FILTER.OVERSTAY]: "react_reception_filter_overstay",
};

export const getFilterLabelKey = (filter) =>
  FILTER_LABEL_KEYS[filter] || FILTER_LABEL_KEYS[RECEPTION_FILTER.ALL];

// ── The five stat tiles (§3) ─────────────────────────────────────────────────────────────────
//
// THE TILES AND THE TABS READ THE SAME `counts` OBJECT. The API returns all seven counts once,
// precisely so the two regions cannot disagree — five of them are also drawn as tiles. Nothing
// here recomputes a count from the rows on screen.
//
// "They describe the whole day and site, regardless of `filter` and `q`. A tab badge answers
//  'how many are behind this tab' — one that shrank as you typed would be answering a different
//  question. Only `items` narrows."
// `icon` is the prototype's glyph for each tile. Kept as characters rather than SVGs because they
// are decorative marks beside a label that already says what the number is — an icon font or five
// more files would be weight for nothing.
export const STAT_TILES = [
  { key: "onSite", countKey: "onSite", tone: "onsite", icon: "●", labelKey: "react_reception_stat_on_site" },
  { key: "expected", countKey: "expected", tone: "expected", icon: "⏱", labelKey: "react_reception_stat_expected" },
  { key: "walkIns", countKey: "walkIns", tone: "walkins", icon: "+", labelKey: "react_reception_stat_walk_ins" },
  { key: "vip", countKey: "vip", tone: "vip", icon: "★", labelKey: "react_reception_stat_vip" },
  { key: "overstay", countKey: "overstay", tone: "overstay", icon: "!", labelKey: "react_reception_stat_overstay" },
];

// ── The tiles change TENSE with the date ─────────────────────────────────────────────────────
//
// A board showing yesterday with "ON SITE 0" is a statement that the building is empty. It is not:
// nobody is on site on a day that has finished. The prototype rewrites the labels, and it is right
// to — "Were on site" is the only reading of that number that is true.
//
// Only three of the five move. "Walk-ins" and "VIPs" are counts of things that happened, and read
// correctly in any tense.
export const RELATIVE_DAY = { PAST: "past", TODAY: "today", FUTURE: "future" };

const TENSE_LABEL_KEYS = {
  onSite: {
    past: "react_reception_stat_on_site_past",
    future: "react_reception_stat_on_site_future",
  },
  expected: { past: "react_reception_stat_expected_past" },
  overstay: { past: "react_reception_stat_overstay_past" },
};

export const getStatLabelKey = (tile, relativeDay) =>
  TENSE_LABEL_KEYS[tile.key]?.[relativeDay] || tile.labelKey;

// Past, today or future — WITHOUT comparing clocks with the server.
//
// `is_today` is authoritative and comes from the API (§3), so it settles the "today" case on its
// own. The only thing left to decide is which side of today the other dates fall, and that is
// answered by comparing against whichever date the server last told us was today — never against
// the browser's clock, which can be a day out and is a different machine's opinion anyway.
export const getRelativeDay = (dateISO, isToday, serverTodayISO) => {
  if (isToday) return RELATIVE_DAY.TODAY;
  if (!dateISO || !serverTodayISO) return RELATIVE_DAY.TODAY;

  return String(dateISO) < String(serverTodayISO) ? RELATIVE_DAY.PAST : RELATIVE_DAY.FUTURE;
};

// How fresh the board is, in words. The prototype's footer reads "Updated 2 seconds ago", and on a
// screen that polls, "how long ago" is the question — a raw timestamp makes the reader subtract.
//
// ⚠ `as_at` IS UTC AND CARRIES NO ZONE ("2026-09-09 09:41:02"). Handing that to `new Date()`
// makes the browser read it as LOCAL time, which in Dublin in summer is an hour out and in India
// five and a half — the footer would claim the board was refreshed hours ago or in the future. The
// "Z" is what stops that.
export const formatRelativeTime = (asAt, now = Date.now()) => {
  if (!asAt) return null;

  const parsed = Date.parse(`${String(asAt).trim().replace(" ", "T")}Z`);
  if (Number.isNaN(parsed)) return null;

  const seconds = Math.round((now - parsed) / 1000);

  // A clock a little ahead of the server reads as the future; "just now" is truer than "in 3
  // seconds" and does not make anybody doubt the screen.
  if (seconds < 45) return { key: "react_reception_just_now", value: null };
  if (seconds < 3600) return { key: "react_reception_mins_ago", value: Math.round(seconds / 60) };
  if (seconds < 86400) return { key: "react_reception_hours_ago", value: Math.round(seconds / 3600) };

  return { key: "react_reception_days_ago", value: Math.round(seconds / 86400) };
};

// "Michael Walsh" → "M. Walsh". The host column is narrow and the surname is what a receptionist
// matches on; the full name is on the detail panel, where there is room for it.
//
// A single-word name is left alone — "M." on its own identifies nobody.
export const abbreviateName = (name) => {
  const parts = String(name ?? "").trim().split(/\s+/).filter(Boolean);

  if (parts.length < 2) return parts[0] || "";

  return `${parts[0][0]}. ${parts[parts.length - 1]}`;
};

// The prototype's tiles carry trend chips ("12% up", "vs avg"). §9: there is still no source for a
// rolling per-site average, so they are deliberately NOT rendered. An invented trend on a
// reception screen is a number somebody will repeat in a meeting.

// ── Row status (§4) ──────────────────────────────────────────────────────────────────────────
//
// `cancelled` and `no_show` are not on the board at all, so they are absent here by design.
export const ROW_STATUS = {
  EXPECTED: "expected",
  CHECKED_IN: "checked_in",
  CHECKED_OUT: "checked_out",
};

const STATUS_LABEL_KEYS = {
  [ROW_STATUS.EXPECTED]: "react_reception_status_expected",
  [ROW_STATUS.CHECKED_IN]: "react_reception_status_checked_in",
  [ROW_STATUS.CHECKED_OUT]: "react_reception_status_checked_out",
};

export const getStatusLabelKey = (status) =>
  STATUS_LABEL_KEYS[status] || "react_reception_status_expected";

// OVERSTAY IS A FLAG, NEVER A STATUS (§4).
//
// "Overstays stay on the board and on the muster, flagged, never auto-cleared — because dropping
//  a still-present person from an evacuation list is unsafe."
//
// So a row can be `checked_in` AND overstaying, and the badge has to say both. Treating overstay
// as a fourth status would drop the person off the "on site" count, which is the count a fire
// warden acts on.
export const isOverstaying = (row) => row?.isOverstay === true;

// Minutes → "2H 18M", the prototype's format. A receptionist reads a duration, not a count: "74
// minutes" needs converting in your head before it means anything, and the number that matters
// ("over two hours") is the part that has to land first.
//
// Under an hour it stays in minutes, because "0H 42M" is worse than "42M".
export const formatOverstay = (minutes) => {
  const total = Math.max(0, Math.round(Number(minutes) || 0));

  if (total < 60) return `${total}M`;

  return `${Math.floor(total / 60)}H ${total % 60}M`;
};

// Which pill a row earns. VIP wins over the plain status because it is what the desk needs to see
// first; overstay wins over everything, because it is the only one that needs acting on.
export const getRowTone = (row) => {
  if (!row) return "expected";
  if (isOverstaying(row)) return "overstay";
  if (row.isVip) return "vip";
  if (row.status === ROW_STATUS.CHECKED_IN) return "onsite";
  if (row.status === ROW_STATUS.CHECKED_OUT) return "signedout";

  return "expected";
};

// ── Paging (§3) ──────────────────────────────────────────────────────────────────────────────
//
// `per_page` defaults to 200 HERE, not to the module's usual 25, and 200 is also the maximum.
// "Send no per_page and you get exactly the board you got before paging existed."
//
// A bad value is a 422, never a silent correction: `page=0` and `per_page=1000` are both refused
// rather than quietly becoming 1 and 200.
export const DEFAULT_PER_PAGE = 200;
export const MAX_PER_PAGE = 200;

// ── Compliance (§4) ──────────────────────────────────────────────────────────────────────────
//
// FOUR LINES, AND ONLY TWO ARE REAL.
//
// `photo_captured` and the badge pair come from the kiosk. `hs_acknowledged` and `nda_signed` are
// ABSENT, not `false` — nothing writes them yet:
//
//   "`false` would say 'we asked and they refused', which is a different fact about a person
//    standing at a desk, and a red cross on your panel would state it as truth."
//
// So the panel must distinguish three states, not two. `NOT_RECORDED` is why this returns a
// string rather than a boolean.
export const COMPLIANCE_STATE = {
  YES: "yes",
  NO: "no",
  NOT_RECORDED: "not_recorded",
};

export const getComplianceState = (compliance, key) => {
  if (!compliance || !(key in compliance)) return COMPLIANCE_STATE.NOT_RECORDED;

  return compliance[key] ? COMPLIANCE_STATE.YES : COMPLIANCE_STATE.NO;
};

// A walk-in's `require_hs` / `require_nda` switches are NOT these (§5). They record what the desk
// said was required, not what anybody signed, and must never render as a tick here.

// ── Initials for the avatar chip ─────────────────────────────────────────────────────────────
//
// `String(null)` is `"null"`, so a null name would produce the initial "N". Guarded with `?? ""`
// rather than a default parameter, which only covers `undefined`.
export const getInitials = (name) =>
  String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

// Avatar colour, chosen from the name so the same person is the same colour on every render
// without the API having to carry one.
const AVATAR_TONES = ["blue", "teal", "amber", "coral", "violet"];

export const getAvatarTone = (name) => {
  const text = String(name ?? "");
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash + text.charCodeAt(index)) % AVATAR_TONES.length;
  }

  return AVATAR_TONES[hash];
};

// ── Count-sensitive copy ─────────────────────────────────────────────────────────────────────
// This app interpolates with `.replace("%s", n)` and has no plural machinery, so "1 visitors"
// needs a `_one` sibling key. Same helper as the Visitor module.
export const pluralKey = (key, count) => (Number(count) === 1 ? `${key}_one` : key);

export const pluralText = (t, key, count) =>
  t(pluralKey(key, count)).replace("%s", String(count));
